import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  ApiError,
  AsyncDataItem,
  AsyncDataItemState,
  IBooking,
  IBookingListParams,
  IPaginationMeta,
  idleDataItem,
  isError,
  isLoading,
  wrapAsAsyncDataItem
} from '@pharvio/core/shared';
import { EMPTY, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BookingsDataService } from '../../data/repositories/bookings-data.service';
import { BOOKINGS_DATA_SERVICE } from '../../data/tokens/bookings-service.token';
import {
  CancelBooking,
  CompleteBooking,
  ConfirmBooking,
  CreateBooking,
  DeclineBooking,
  LoadBooking,
  LoadBookings,
  NoShowBooking,
  ProposeBookingTime,
  SelectBooking,
  StartBooking
} from './bookings.actions';

export interface BookingsStateModel {
  bookings: AsyncDataItem<IBooking[]>;
  pagination: IPaginationMeta | null;
  /** The filter the current page was fetched with, for reload-in-place. */
  params: IBookingListParams;
  detail: AsyncDataItem<IBooking | null>;
  /**
   * The invoice a cancel or decline left unrefunded.
   *
   * `depositInvoiceId` rides on the cancel/decline response and is not part of
   * the booking, so it has nowhere else to live. Non-null means the customer
   * paid a deposit and **nothing has been refunded** — the page prompts for a
   * refund ticket against it, then clears this.
   */
  pendingRefundInvoiceId: string | null;
  selectedId: string | null;
}

@State<BookingsStateModel>({
  name: 'bookings',
  defaults: {
    bookings: idleDataItem([]),
    pagination: null,
    params: {},
    detail: idleDataItem(null),
    pendingRefundInvoiceId: null,
    selectedId: null
  }
})
@Injectable()
export class BookingsState {
  private readonly service: BookingsDataService = inject(BOOKINGS_DATA_SERVICE);

  @Selector() static bookings(
    s: BookingsStateModel
  ): AsyncDataItem<IBooking[]> {
    return s.bookings;
  }

  @Selector() static loading(s: BookingsStateModel): boolean {
    return isLoading(s.bookings);
  }

  @Selector() static error(s: BookingsStateModel): ApiError | null {
    return isError(s.bookings) ? s.bookings.error : null;
  }

  @Selector() static pagination(s: BookingsStateModel): IPaginationMeta | null {
    return s.pagination;
  }

  @Selector() static params(s: BookingsStateModel): IBookingListParams {
    return s.params;
  }

  @Selector() static detail(
    s: BookingsStateModel
  ): AsyncDataItem<IBooking | null> {
    return s.detail;
  }

  @Selector() static detailLoading(s: BookingsStateModel): boolean {
    return isLoading(s.detail);
  }

  @Selector() static detailError(s: BookingsStateModel): ApiError | null {
    return isError(s.detail) ? s.detail.error : null;
  }

  @Selector() static pendingRefundInvoiceId(
    s: BookingsStateModel
  ): string | null {
    return s.pendingRefundInvoiceId;
  }

  @Selector() static selectedId(s: BookingsStateModel): string | null {
    return s.selectedId;
  }

  /**
   * Not TTL-cached. The list is filtered from the URL and a booking moves on
   * its own — a deposit webhook lands, a job expires an unpaid one — so a
   * stale page here shows actions the server will refuse.
   */
  @Action(LoadBookings)
  load(ctx: StateContext<BookingsStateModel>, { params }: LoadBookings) {
    ctx.patchState({
      bookings: wrapAsAsyncDataItem({ data: ctx.getState().bookings.data }),
      params
    });

    return this.service.getBookings(params).pipe(
      tap((page) =>
        ctx.patchState({
          bookings: wrapAsAsyncDataItem({
            data: page.data,
            state: AsyncDataItemState.LOADED
          }),
          pagination: page.meta
        })
      ),
      catchError((error) => {
        ctx.patchState({
          bookings: wrapAsAsyncDataItem({
            data: [],
            state: AsyncDataItemState.ERROR,
            error
          }),
          pagination: null
        });
        return EMPTY;
      })
    );
  }

  @Action(LoadBooking)
  loadOne(ctx: StateContext<BookingsStateModel>, { id, reload }: LoadBooking) {
    const current = ctx.getState().detail;
    // Keep the row already on screen visible while it refreshes; blanking the
    // page on every poll would make the deposit wait flicker.
    const keep = !reload && current.data?.id === id ? current.data : null;

    ctx.patchState({
      detail: wrapAsAsyncDataItem({ data: keep }),
      selectedId: id
    });

    return this.service.getBooking(id).pipe(
      tap((booking) => {
        ctx.patchState({
          detail: wrapAsAsyncDataItem({
            data: booking,
            state: AsyncDataItemState.LOADED
          })
        });
        this.upsertRow(ctx, booking);
      }),
      catchError((error) => {
        ctx.patchState({
          detail: wrapAsAsyncDataItem({
            data: null,
            state: AsyncDataItemState.ERROR,
            error
          })
        });
        return EMPTY;
      })
    );
  }

  // Every mutation below rethrows. The pages turn BOOKING_INVALID_TRANSITION
  // into a refetch, SHIFT_NOT_OPEN into "open a till first" and
  // BOOKING_NOT_YET_DUE into a disabled no-show — none of which reaches the
  // subscriber if the error is swallowed here.

  @Action(CreateBooking)
  create(ctx: StateContext<BookingsStateModel>, { payload }: CreateBooking) {
    return this.service.createBooking(payload).pipe(
      tap((result) => {
        this.upsertRow(ctx, result.booking);
        ctx.patchState({
          detail: wrapAsAsyncDataItem({
            data: result.booking,
            state: AsyncDataItemState.LOADED
          }),
          selectedId: result.booking.id
        });
      }),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(ConfirmBooking)
  confirm(ctx: StateContext<BookingsStateModel>, { id }: ConfirmBooking) {
    return this.service.confirm(id).pipe(
      tap((booking) => this.apply(ctx, booking)),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(ProposeBookingTime)
  proposeTime(
    ctx: StateContext<BookingsStateModel>,
    { id, payload }: ProposeBookingTime
  ) {
    return this.service.proposeTime(id, payload).pipe(
      tap((booking) => this.apply(ctx, booking)),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(StartBooking)
  start(ctx: StateContext<BookingsStateModel>, { id }: StartBooking) {
    return this.service.start(id).pipe(
      tap((booking) => this.apply(ctx, booking)),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * The one transition with a branch: a balance that is due raises
   * `balanceOrder` and leaves the booking in `pending_balance`; nothing due
   * completes it outright. The page reads the resulting status off the detail
   * slice rather than assuming either.
   */
  @Action(CompleteBooking)
  complete(
    ctx: StateContext<BookingsStateModel>,
    { id, payload }: CompleteBooking
  ) {
    return this.service.complete(id, payload).pipe(
      tap((result) => this.apply(ctx, result.booking)),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(DeclineBooking)
  decline(
    ctx: StateContext<BookingsStateModel>,
    { id, payload }: DeclineBooking
  ) {
    return this.service.decline(id, payload).pipe(
      tap((result) => {
        this.apply(ctx, result);
        ctx.patchState({ pendingRefundInvoiceId: result.depositInvoiceId });
      }),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(NoShowBooking)
  noShow(ctx: StateContext<BookingsStateModel>, { id }: NoShowBooking) {
    return this.service.noShow(id).pipe(
      // A no-show keeps the deposit, so `depositInvoiceId` comes back null and
      // there is deliberately no refund prompt.
      tap((result) => this.apply(ctx, result)),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(CancelBooking)
  cancel(ctx: StateContext<BookingsStateModel>, { id, payload }: CancelBooking) {
    return this.service.cancel(id, payload).pipe(
      tap((result) => {
        this.apply(ctx, result);
        ctx.patchState({ pendingRefundInvoiceId: result.depositInvoiceId });
      }),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(SelectBooking)
  select(ctx: StateContext<BookingsStateModel>, { id }: SelectBooking) {
    // Leaving a booking also drops any refund prompt it raised: the page that
    // would have shown it is gone.
    ctx.patchState({ selectedId: id, pendingRefundInvoiceId: null });
  }

  /** Folds an updated booking into both the list and the detail slice. */
  private apply(ctx: StateContext<BookingsStateModel>, booking: IBooking): void {
    this.upsertRow(ctx, booking);
    if (ctx.getState().detail.data?.id === booking.id) {
      ctx.patchState({
        detail: wrapAsAsyncDataItem({
          data: booking,
          state: AsyncDataItemState.LOADED
        })
      });
    }
  }

  private upsertRow(
    ctx: StateContext<BookingsStateModel>,
    booking: IBooking
  ): void {
    const current = ctx.getState().bookings.data;
    if (!current.some((b) => b.id === booking.id)) return;
    ctx.patchState({
      bookings: wrapAsAsyncDataItem({
        data: current.map((b) => (b.id === booking.id ? booking : b)),
        state: AsyncDataItemState.LOADED
      })
    });
  }
}
